import type { QueryClient } from '@tanstack/react-query'

export interface RealtimeNotification {
  notification_id?: string
  id?: string
  lifecycle_state?: string
  status?: string
  version?: number
  server_timestamp?: number
  requires_action?: boolean
  [key: string]: unknown
}

export interface NotificationRealtimeEvent {
  type: 'notification.created' | 'notification.updated' | 'notification.deleted'
  event_id: string
  version: number
  server_timestamp: number
  stage: string
  notification: RealtimeNotification
}

const idOf = (value: unknown) => {
  if (!value || typeof value !== 'object') return undefined
  const item = value as RealtimeNotification
  return item.notification_id || item.id
}

const replaceExisting = (
  value: unknown,
  notification: RealtimeNotification,
  remove: boolean
): unknown => {
  if (Array.isArray(value)) {
    return value
      .filter(item => !(remove && idOf(item) === idOf(notification)))
      .map(item =>
        idOf(item) === idOf(notification)
          ? { ...(item as object), ...notification }
          : replaceExisting(item, notification, remove)
      )
  }
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      replaceExisting(child, notification, remove),
    ])
  )
}

const prepend = (items: unknown, notification: RealtimeNotification) => {
  if (!Array.isArray(items)) return [notification]
  if (items.some(item => idOf(item) === idOf(notification))) return items
  return [notification, ...items]
}

export const applyNotificationRealtimeEvent = (
  queryClient: QueryClient,
  event: NotificationRealtimeEvent
) => {
  const notification = event.notification
  const archived =
    notification.lifecycle_state === 'archived' ||
    notification.status === 'archived'
  const deleted = event.type === 'notification.deleted'

  queryClient
    .getQueryCache()
    .findAll({ queryKey: ['notifications'] })
    .forEach(query => {
      const view = query.queryKey[2]
      const next = replaceExisting(query.state.data, notification, deleted)
      if (!next || deleted) {
        queryClient.setQueryData(query.queryKey, next)
        return
      }

      const response = next as any
      const data = response?.data
      if (view === 'archive') {
        data.notifications = archived
          ? prepend(data.notifications, notification)
          : replaceExisting(data.notifications, notification, true)
      } else if (view === 'team_feed') {
        data.notifications = archived
          ? replaceExisting(data.notifications, notification, true)
          : prepend(data.notifications, notification)
      } else if (view === 'my_work') {
        const buckets = [
          'requires_action',
          'active_cases',
          'shift_alerts',
          'informational',
        ]
        buckets.forEach(bucket => {
          data[bucket] = replaceExisting(data[bucket], notification, true)
        })
        if (!archived) {
          const bucket = notification.requires_action
            ? 'requires_action'
            : 'informational'
          data[bucket] = prepend(data[bucket], notification)
        }
      } else if (Array.isArray(response?.items) && !archived) {
        response.items = prepend(response.items, notification)
      }
      queryClient.setQueryData(query.queryKey, next)
    })
}
